/**
 * NotFoundRedesign — v3 liquid glass.
 *
 * 404 sits inside the same Atmosphere as the rest of marketing. NavTiles
 * become small LiquidGlass tiles with the homepage's icon-orb treatment.
 */
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Home, BookOpen, MessageCircle } from "lucide-react";
import { MarketingLayout } from "@/redesign/layouts";
import { RdButton } from "@/redesign/components";
import { LiquidGlass } from "./LiquidGlass";
import { GlassStage, Reveal } from "./_glassChrome";

export default function NotFoundRedesign() {
  return (
    <MarketingLayout
      cta={
        <>
          <Link
            to="/auth"
            className="rounded-md px-3 py-1.5 text-[13.5px] font-medium text-[hsl(var(--rd-text-2))] hover:text-[hsl(var(--rd-text-1))]"
          >
            Log in
          </Link>
          <RdButton size="sm" asChild>
            <Link to="/auth">Get started</Link>
          </RdButton>
        </>
      }
    >
      <GlassStage>
        <div className="relative mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-3xl flex-col items-start justify-center px-6 py-24">
          <Reveal>
            <div className="font-mono text-[12px] uppercase tracking-[0.2em] text-[hsl(var(--rd-text-3))]">
              Error · 404
            </div>
          </Reveal>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="mt-6 text-[72px] font-semibold leading-[0.95] tracking-tight text-[hsl(var(--rd-text-1))] md:text-[120px]"
          >
            Lost in
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(110deg, hsl(var(--rd-accent-200)) 0%, hsl(var(--rd-accent-500)) 45%, hsl(var(--rd-accent-300)) 100%)",
              }}
            >
              the feed.
            </span>
          </motion.h1>
          <Reveal delay={0.28}>
            <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-[hsl(var(--rd-text-2))]">
              That page doesn't exist — or moved during the redesign. Try one of these instead:
            </p>
          </Reveal>

          <div className="mt-10 grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
            <NavTile to="/" icon={Home} title="Home" subtitle="Back to creatorvault.ai" i={0} />
            <NavTile to="/docs" icon={BookOpen} title="Docs" subtitle="API & guides" i={1} />
            <NavTile to="/contact" icon={MessageCircle} title="Contact" subtitle="Talk to us" i={2} />
          </div>

          <Reveal delay={0.5}>
            <RdButton variant="ghost" className="mt-10" asChild>
              <Link to="/" className="inline-flex items-center gap-2">
                <ArrowLeft className="h-3.5 w-3.5" />
                Go back home
              </Link>
            </RdButton>
          </Reveal>
        </div>
      </GlassStage>
    </MarketingLayout>
  );
}

function NavTile({
  to,
  icon: Icon,
  title,
  subtitle,
  i,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  i: number;
}) {
  const reduce = useReducedMotion();
  return (
    <Reveal delay={0.34 + 0.06 * i}>
      <motion.div
        whileHover={reduce ? undefined : { y: -2 }}
        transition={{ type: "spring", stiffness: 280, damping: 22 }}
      >
        <Link
          to={to}
          className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--rd-accent-500))] rounded-[14px]"
        >
          <LiquidGlass radius={14} elevation="low" className="p-4">
            <div className="flex items-start gap-3">
              <div
                className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] text-[hsl(var(--rd-accent-200))]"
                style={{
                  background:
                    "linear-gradient(160deg, hsl(var(--rd-accent-500) / 0.28) 0%, hsl(var(--rd-accent-700) / 0.18) 100%)",
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.18), 0 0 0 1px hsl(var(--rd-accent-500) / 0.25)",
                }}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[13.5px] font-semibold text-[hsl(var(--rd-text-1))] transition-colors group-hover:text-[hsl(var(--rd-accent-300))]">
                  {title}
                </div>
                <div className="mt-0.5 text-[12.5px] text-[hsl(var(--rd-text-3))]">{subtitle}</div>
              </div>
            </div>
          </LiquidGlass>
        </Link>
      </motion.div>
    </Reveal>
  );
}
